import { Injectable, PLATFORM_ID, computed, inject, signal } from "@angular/core";
import { isPlatformBrowser } from "@angular/common";
import { BaseStore } from "@client";
import { TOKEN_KEY } from "@common";
import { UpdateUserDto, UserDto, UserService } from "@namdorang/api-client";
import { lastValueFrom, map } from "rxjs";

type State = {
  user: UserDto | null;
};

@Injectable({ providedIn: "root" })
export class UserStore extends BaseStore<State> {
  private readonly userService = inject(UserService);
  private readonly platformId = inject(PLATFORM_ID);
  user = computed(() => this.state()?.user);
  user$ = this.state$.pipe(map((state) => state.user));
  fetched = signal<boolean>(false);
  hasToken = signal<boolean>(false);

  constructor() {
    super({ user: null });

    // SSR에서는 localStorage가 없으므로 로그인/비로그인 상태를 확정하지 않는다.
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    const accessToken = localStorage.getItem(TOKEN_KEY.ACCESS_TOKEN_KEY);
    if (!accessToken) {
      this.fetched.set(true);
      return;
    }

    this.hasToken.set(true);
    void this.fetchUser();
  }

  setMember(user: UserDto | null) {
    this.updateState({ user });
  }

  async fetchUser() {
    try {
      const user = await lastValueFrom(this.userService.userControllerGetMe());
      if (!user) {
        this.setUser(null);
        return null;
      }
      this.setUser(user);
      this.hasToken.set(true);
      return user;
    } catch (error) {
      this.setUser(null);
      if (this.isAuthExpired(error)) {
        this.clearAuth();
      }
      return null;
    } finally {
      this.fetched.set(true);
    }
  }

  async update(body: UpdateUserDto) {
    const user = await lastValueFrom(
      this.userService.userControllerUpdate({
        body,
      })
    );

    this.setUser(user);
  }

  async logout() {
    this.clearAuth();
  }

  setUser(user: UserDto | null) {
    this.updateState({ user });
  }

  clearAuth() {
    if (isPlatformBrowser(this.platformId)) {
      localStorage.removeItem(TOKEN_KEY.ACCESS_TOKEN_KEY);
      localStorage.removeItem(TOKEN_KEY.REFRESH_TOKEN_KEY);
    }
    this.hasToken.set(false);
    this.setMember(null);
    this.fetched.set(true);
  }

  private isAuthExpired(error: unknown) {
    const status = (error as { status?: number })?.status;
    return status === 401 || status === 498;
  }
}
